import type { YearMonth } from '../model';
import type { FileProblem, UploadedFile } from '../upload';

/** What the list says about one file (i18n key `upload.status.<status>`). */
export type FileStatus =
  | 'reading'
  | 'read'
  | 'scanToRead'
  /** A digital PDF read, but its totals don't add up (or no amounts): offered a reading as a scan (persona L1). */
  | 'doubtfulToRead'
  | 'scanReading'
  /** Waiting its turn: scans are read one at a time. */
  | 'scanQueued'
  | 'scanToCheck'
  | 'scanRead'
  | 'scanFailed'
  /** A digital PDF in a layout we don't know, its numbers found by their labels (src/fallback): to check, then checked. */
  | 'labelsToCheck'
  | 'labelsRead'
  | 'locked'
  | 'notRecognised'
  /** A Form 106 (yearly tax summary), not a payslip. */
  | 'form106';

export type OcrState = 'offer' | 'cancelled' | 'queued' | 'running' | 'review' | 'confirmed' | 'failed';

/** What a file was read as; shown once it is known (not while reading, not for a file we couldn't read). */
export type DocType = 'payslip' | 'form106';

export interface FileRow {
  /** Index in the uploads (and files, and OCR jobs); undefined while the file is still being read. */
  index: number | undefined;
  name: string;
  /** Pay months read from the payslip content, ascending. */
  months: YearMonth[];
  status: FileStatus;
  problem?: FileProblem;
  /** A Form 106's tax year, when read. */
  taxYear?: number;
  docType?: DocType;
  /**
   * Read, but its period wasn't found: a payslip page without a pay month (its page numbers), or a Form 106 without a
   * tax year. The row says "… not found" and asks for it right there (dead ends rule), never "unknown".
   */
  periodMissing?: { pages: number[] } | 'taxYear';
  /**
   * A digital PDF read where only some pages have no text (scans among them): the readable pages are read, these wait
   * for the offer to read them as images. They never hold the user back (the file is 'read').
   */
  noTextPages?: number[];
}

/** Statuses where the file has been read and its type and period are known (a scan only once confirmed). */
const READ: readonly FileStatus[] = ['read', 'doubtfulToRead', 'scanRead', 'labelsRead', 'form106'];

/**
 * The uploaded-files list, sorted by the earliest pay month read from each file's content (never its
 * name). Files whose month isn't known (still reading, not recognised, a scan not yet confirmed) come
 * last, in upload order. `uploads` must already include confirmed OCR pages (unconfirmed ones give
 * no payslips, so no month); `ocr` gives each OCR file's state by index.
 */
export function fileRows(uploads: readonly UploadedFile[], ocr: (index: number) => OcrState | undefined, pending: readonly string[]): FileRow[] {
  const rows: FileRow[] = uploads.map((u, index) => {
    const months = [...new Set(u.vendor ? u.payslips.map((p) => p.fields.payMonth).filter((m): m is YearMonth => m !== undefined) : [])].sort();
    const noTextPages = noTextPagesOf(u, ocr(index));
    const status = noTextPages.length ? { status: 'read' as const } : statusOf(u, ocr(index));
    return { index, name: u.name, months, ...status, ...(u.form106?.fields.taxYear ? { taxYear: u.form106.fields.taxYear } : {}), ...readAs(u, status.status), ...(noTextPages.length ? { noTextPages } : {}) };
  });
  const known = rows.filter((r) => r.months.length > 0).sort((a, b) => (a.months[0] ?? '').localeCompare(b.months[0] ?? '') || (a.index ?? 0) - (b.index ?? 0));
  const unknown = rows.filter((r) => r.months.length === 0);
  return [...known, ...unknown, ...pending.map((name): FileRow => ({ index: undefined, name, months: [], status: 'reading' }))];
}

/**
 * The pages of a file with payslips already read that have no text and are still waiting for the offer to read them as
 * images (not the doubtful digital pages: those were read, and the reading is offered a second look).
 */
export function noTextPagesOf(u: Pick<UploadedFile, 'ocr' | 'ocrPages' | 'ocrRetry' | 'payslips' | 'form106'>, ocr: OcrState | undefined): number[] {
  if (!u.ocr || u.form106 || u.payslips.length === 0 || !u.ocrPages) return [];
  if (ocr !== undefined && ocr !== 'offer' && ocr !== 'cancelled') return [];
  return u.ocrPages.filter((p) => !(u.ocrRetry ?? []).includes(p));
}

function readAs(u: UploadedFile, status: FileStatus): Pick<FileRow, 'docType' | 'periodMissing'> {
  if (!READ.includes(status)) return {};
  if (u.form106) return { docType: 'form106', ...(u.form106.fields.taxYear ? {} : { periodMissing: 'taxYear' as const }) };
  const pages = u.payslips.filter((p) => !p.fields.payMonth).map((p) => p.page);
  return { docType: 'payslip', ...(pages.length ? { periodMissing: { pages } } : {}) };
}

function statusOf(u: UploadedFile, ocr: OcrState | undefined): Pick<FileRow, 'status' | 'problem'> {
  if (u.form106) return { status: 'form106' };
  if (u.labelsRead) {
    if (ocr === 'confirmed') return { status: 'labelsRead' };
    if (ocr === 'review') return { status: 'labelsToCheck' };
    return { status: 'reading' };
  }
  if (u.ocr) {
    if (ocr === 'running') return { status: 'scanReading' };
    if (ocr === 'queued') return { status: 'scanQueued' };
    if (ocr === 'review') return { status: 'scanToCheck' };
    if (ocr === 'confirmed') return { status: 'scanRead' };
    if (ocr === 'failed') return { status: 'scanFailed' };
    return { status: u.ocrRetry?.length ? 'doubtfulToRead' : 'scanToRead' };
  }
  // A password-protected PDF: asked for its password on its row (src/ui/app.ts), not "not recognised".
  if (u.problem === 'needsPassword' || u.problem === 'wrongPassword') return { status: 'locked', problem: u.problem };
  if (u.problem || !u.vendor || u.payslips.length === 0) return { status: 'notRecognised', problem: u.problem ?? 'unknownLayout' };
  return { status: 'read' };
}

/**
 * The pages of a file's payslips the read gate turned away (Matt, 2026-10-21: decided per payslip), named on its row
 * whatever the file's read status (a digital file, a scan or a doubtful page read again): none on a row that says the
 * file wasn't read at all or is locked (its reason says it).
 */
export function turnedAwayPages(status: FileStatus, merged: { unrecognised?: { pages?: readonly number[] } } | undefined): number[] {
  if (status === 'notRecognised' || status === 'locked' || status === 'reading') return [];
  return [...(merged?.unrecognised?.pages ?? [])];
}

/** The same file chosen twice (name, size and last change all equal). */
export const sameFile = (a: Pick<File, 'name' | 'size' | 'lastModified'>, b: Pick<File, 'name' | 'size' | 'lastModified'>) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
